import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiTarget, FiUserPlus, FiUserCheck, FiX, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { LeadExecutionEngine } from '../../api/LeadExecutionEngine';
import { UserExecutionEngine } from '../../api/UserExecutionEngine';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

export default function AssumeLeadModal({ lead, onClose, onRefresh }) {
    const navigate = useNavigate();
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAgent, setSelectedAgent] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const [showTransfer, setShowTransfer] = useState(false);

    const loadAgentsForTransfer = async () => {
        if (agents.length > 0) {
            setShowTransfer(true);
            return;
        }
        
        const fetchPromise = async () => {
            setLoading(true);
            const brokers = await UserExecutionEngine.listarAgentes();
            setAgents(brokers || []);
            setLoading(false);
            if (brokers?.length > 0) {
                setShowTransfer(true);
            } else {
                throw new Error("Nenhum usuário válido encontrado para delegar.");
            }
        };

        toast.promise(fetchPromise(), {
            loading: 'Localizando usuários válidos...',
            success: 'Tropas localizadas!',
            error: (err) => err.message || 'Falha ao localizar usuários.'
        });
    };

    const handleAssume = async () => {
        setActionLoading(true);
        let loggedName = 'OPERADOR_MASTER';
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const dec = jwtDecode(token);
                loggedName = dec.name || dec.nome || dec.usuario || 'MASTER';
            } catch (e) {}
        }
        
        const assumePromise = async () => {
            const ok = await LeadExecutionEngine.atualizarStatus(lead._id, 'TRIAGEM', loggedName, 'Lead assumido diretamente pelo Gestor/Master');
            if (ok) {
                console.log(`[ASSUME] Sucesso ao assumir o lead ${lead._id} para o usuário ${loggedName}`);
                navigate('/leads/gestao');
            } else {
                console.error(`[ASSUME] Falha crítica ao tentar assumir o lead ${lead._id}`);
                throw new Error("Falha ao assumir lead");
            }
        };

        toast.promise(assumePromise(), {
            loading: 'Assumindo lead...',
            success: 'LEAD ASSUMIDO COM SUCESSO!',
            error: 'FALHA AO ASSUMIR LEAD'
        }).finally(() => setActionLoading(false));
    };

    const handleTransfer = async () => {
        if (!selectedAgent) {
            toast.error("Selecione um agente para transferir o lead!");
            return;
        }
        setActionLoading(true);
        const agentName = agents.find(a => a._id === selectedAgent)?.name || selectedAgent;
        const transferPromise = async () => {
            const ok = await LeadExecutionEngine.atualizarStatus(lead._id, 'TRIAGEM', agentName, `Missão delegada ao corretor: ${agentName}`);
            if (ok) {
                console.log(`[DELEGAR] Sucesso ao delegar o lead ${lead._id} para o agente ${agentName}`);
                onRefresh && onRefresh();
                onClose();
            } else {
                console.error(`[DELEGAR] Falha crítica ao tentar delegar o lead ${lead._id} para ${agentName}`);
                throw new Error("Falha ao delegar lead");
            }
        };

        toast.promise(transferPromise(), {
            loading: 'Delegando lead...',
            success: `LEAD DELEGADO PARA ${agentName.toUpperCase()}!`,
            error: 'FALHA AO DELEGAR LEAD'
        }).finally(() => setActionLoading(false));
    };

    return (
        <AnimatePresence>
            <motion.div 
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl"
            >
                <motion.div 
                    initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
                    className="w-full max-w-lg bg-[var(--bg-card)] border-2 border-[var(--border-color)] rounded-[3rem] overflow-hidden flex flex-col shadow-2xl relative"
                >
                    <button onClick={onClose} className="absolute top-6 right-6 p-3 hover:bg-red-500 hover:text-white text-[var(--text-primary)] rounded-full transition-all border border-[var(--border-color)] group z-10 bg-[var(--bg-main)]">
                        <FiX size={20} className="group-hover:rotate-90 transition-transform" />
                    </button>

                    <div className="p-8 text-center border-b border-[var(--border-color)] bg-[var(--text-primary)]/[0.03]">
                        <div className="w-20 h-20 bg-emerald-500 mx-auto rounded-full flex items-center justify-center text-white shadow-[0_0_40px_rgba(16,185,129,0.3)] mb-4">
                            <FiTarget size={40} className="animate-pulse" />
                        </div>
                        <h2 className="text-3xl font-black italic tracking-tighter text-[var(--text-primary)] uppercase">
                            DIRETRIZ DE ALVO
                        </h2>
                        <p className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-[0.2em] mt-2">
                            Ação Tática para o Lead: <span className="text-[var(--accent-color)]">{lead.name || lead.nome}</span>
                        </p>
                    </div>

                    <div className="p-8 space-y-8 bg-[var(--bg-main)]">
                        {/* OPÇÃO 1: EU ASSUMO */}
                        <div className="p-6 border border-[var(--border-color)] rounded-3xl bg-[var(--bg-card)] hover:border-emerald-500 transition-colors group">
                            <button 
                                onClick={handleAssume}
                                disabled={actionLoading}
                                className="w-full flex items-center justify-between text-left"
                            >
                                <div>
                                    <h3 className="text-xl font-black uppercase tracking-tight text-[var(--text-primary)] flex items-center gap-2">
                                        <FiUserCheck className="text-emerald-500" />
                                        EU ASSUMO A MISSÃO
                                    </h3>
                                    <p className="text-[10px] uppercase font-bold text-[var(--text-secondary)] tracking-widest mt-1">
                                        O lead será transportado para sua Esteira
                                    </p>
                                </div>
                                <div className="w-10 h-10 bg-[var(--bg-main)] border border-[var(--border-color)] rounded-full flex items-center justify-center group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                                    <FiTarget size={16} />
                                </div>
                            </button>
                        </div>

                        {/* OPÇÃO 2: TRANSFERIR PARA USER */}
                        <div className="p-6 border border-[var(--border-color)] rounded-3xl bg-[var(--bg-card)]">
                            <div className="mb-4">
                                <h3 className="text-lg font-black uppercase tracking-tight text-[var(--text-primary)] flex items-center gap-2">
                                    <FiUserPlus className="text-blue-500" />
                                    DELEGAR PARA OPERADOR
                                </h3>
                                <p className="text-[9px] uppercase font-bold text-[var(--text-secondary)] tracking-widest mt-1">
                                    Transfere o lead para a esteira de outro corretor
                                </p>
                            </div>
                            
                            {!showTransfer ? (
                                <button 
                                    onClick={loadAgentsForTransfer}
                                    disabled={actionLoading}
                                    className="w-full h-12 bg-blue-600/10 hover:bg-blue-600/20 text-blue-500 font-black text-[10px] uppercase tracking-widest rounded-xl transition-all border border-blue-500/30 flex items-center justify-center gap-2"
                                >
                                    {loading ? <FiRefreshCw className="animate-spin" /> : <FiUserPlus />}
                                    {loading ? "LOCALIZANDO..." : "ESCOLHER AGENTE PARA DELEGAR"}
                                </button>
                            ) : (
                                <div className="flex gap-4">
                                    <select 
                                        className="flex-1 bg-[var(--bg-main)] border border-[var(--border-color)] text-[var(--text-primary)] text-xs font-bold rounded-xl px-4 h-12 outline-none focus:border-blue-500 uppercase"
                                        value={selectedAgent}
                                        onChange={(e) => setSelectedAgent(e.target.value)}
                                    >
                                        <option value="">Selecione um Agente...</option>
                                        {agents.map(agent => (
                                            <option key={agent._id} value={agent._id}>{agent.nome || agent.name || agent.usuario}</option>
                                        ))}
                                    </select>
                                    <button 
                                        onClick={handleTransfer}
                                        disabled={actionLoading || !selectedAgent}
                                        className="h-12 px-6 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black text-[10px] uppercase tracking-widest rounded-xl transition-all"
                                    >
                                        {actionLoading ? <FiRefreshCw className="animate-spin" /> : 'DELEGAR'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
